import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/shared/integrations/supabase/client";
import { sameStorageObject } from "@/shared/lib/firebaseStorageUrl";

export interface SurveyAsset {
  id: string;
  name: string;
  category: string;
  primary_image_path: string | null;
}

export interface SurveyAssetPhoto {
  id: string;
  asset_id: string;
  file_path: string;
  document_name: string;
  signedUrl?: string;
  /** `asset_documents.category` — the nameplate / QR marker. */
  category?: string | null;
  /**
   * False for the entry synthesised from `property_assets.asset_photo_url`,
   * which has no `asset_documents` row to carry a tag.
   */
  hasDocumentRow?: boolean;
}

/** Assets grouped by category (lowercased) */
export type AssetsByCategory = Record<
  string,
  { assets: SurveyAsset[]; photos: SurveyAssetPhoto[] }
>;

/**
 * Get a displayable URL for a storage path.
 * If already a URL, return as-is. Otherwise use getPublicUrl (no network call).
 */
function getPhotoUrl(filePath: string): string {
  if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
    return filePath;
  }
  const { data } = supabase.storage
    .from("asset-documents")
    .getPublicUrl(filePath);
  return data?.publicUrl || "";
}

/**
 * Hook: fetches all assets + their photos for a property, grouped by category.
 */
export function usePropertySurveyAssets(propertyId: string | null) {
  return useQuery<AssetsByCategory>({
    queryKey: ["survey-assets", propertyId],
    queryFn: async (): Promise<AssetsByCategory> => {
      if (!propertyId) return {};

      // 1. Fetch assets
      const { data: assetsRaw, error: assetsError } = await supabase
        .from("property_assets")
        .select("id, name, category_id, asset_categories(name), asset_photo_url")
        .eq("property_id", propertyId)
        .order("name", { ascending: true });

      if (assetsError) throw assetsError;
      if (!assetsRaw || assetsRaw.length === 0) return {};

      const assets = assetsRaw.map((a: any) => ({
        ...a,
        category: a.asset_categories?.name ?? null,
        primary_image_path: a.asset_photo_url ?? null,
      }));

      // 2. Fetch photos from asset_documents (batched)
      const assetIds = assets.map((a) => a.id);
      const batchSize = 100;
      let allPhotos: SurveyAssetPhoto[] = [];

      for (let i = 0; i < assetIds.length; i += batchSize) {
        const batch = assetIds.slice(i, i + batchSize);
        const { data: photosData } = await supabase
          .from("asset_documents")
          .select("id, asset_id, file_path, document_name, category")
          .is("deleted_at", null)
          .eq("document_type", "photo")
          .in("asset_id", batch);

        if (photosData) {
          allPhotos.push(
            ...(photosData as SurveyAssetPhoto[]).map((p) => ({
              ...p,
              hasDocumentRow: true,
            })),
          );
        }
      }

      // 3. Resolve URLs synchronously (no network calls)
      for (const photo of allPhotos) {
        if (photo.file_path) {
          photo.signedUrl = getPhotoUrl(photo.file_path);
        }
      }

      // 4. Group by category (lowercased)
      const grouped: AssetsByCategory = {};

      for (const asset of assets) {
        const key = (asset.category || "uncategorized").toLowerCase();
        if (!grouped[key]) {
          grouped[key] = { assets: [], photos: [] };
        }
        grouped[key].assets.push(asset);

        // Add the primary image as a photo entry — but only when no
        // asset_documents row already covers that object. The synthetic entry
        // carries no row id or category, so shadowing a real row would make an
        // otherwise-taggable photo untaggable (the document row is dropped as a
        // duplicate further down).
        if (asset.primary_image_path) {
          const url = getPhotoUrl(asset.primary_image_path);
          const covered = allPhotos.some(
            (p) =>
              p.asset_id === asset.id &&
              sameStorageObject(p.file_path, asset.primary_image_path),
          );
          if (url && !covered) {
            grouped[key].photos.push({
              id: `primary-${asset.id}`,
              asset_id: asset.id,
              file_path: asset.primary_image_path,
              document_name: asset.name,
              signedUrl: url,
              category: null,
              hasDocumentRow: false,
            });
          }
        }
      }

      // Add document photos to their asset's category
      for (const photo of allPhotos) {
        if (!photo.signedUrl) continue;
        const asset = assets.find((a) => a.id === photo.asset_id);
        if (!asset) continue;
        const key = (asset.category || "uncategorized").toLowerCase();
        if (!grouped[key]) {
          grouped[key] = { assets: [], photos: [] };
        }
        const isDuplicate = grouped[key].photos.some(
          (p) => p.file_path === photo.file_path
        );
        if (!isDuplicate) {
          grouped[key].photos.push(photo);
        }
      }

      return grouped;
    },
    enabled: !!propertyId,
    staleTime: 5 * 60 * 1000,
  });
}
